import React from 'react';
import 'katex/dist/katex.min.css';
import Latex from 'react-latex-next';

/**
 * Enhanced LaTeX Renderer with text parsing
 * Converts simple LaTeX notation to HTML for inline display
 * Supports both inline and block rendering modes
 */
const LatexRenderer = ({ children, className = '', mode = 'auto' }) => {
    if (!children) return null;

    // Convert to string
    const content = typeof children === 'string' ? children : String(children);

    // Check if content contains complex LaTeX (equations, tables, etc.)
    const hasComplexLatex = content.includes('\\begin{') ||
        content.includes('\\frac{') ||
        content.includes('\\sum') ||
        content.includes('\\int') ||
        content.includes('\\matrix') ||
        content.includes('\\sqrt') ||
        content.includes('$$') ||
        content.includes('\\[');

    // For block mode or complex LaTeX, render with full LaTeX processor
    if (mode === 'block' || hasComplexLatex) {
        let latexContent = content;

        // Auto-fix: If it contains math commands but no delimiters, wrap in delimiters
        if (!latexContent.includes('$') && !latexContent.includes('\\[')) {
            if (latexContent.includes('\\frac') ||
                latexContent.includes('\\sum') ||
                latexContent.includes('\\int') ||
                latexContent.includes('\\sqrt') ||
                latexContent.includes('\\begin{matrix}') ||
                latexContent.includes('\\begin{pmatrix}')) {

                // Use $ for inline mode, $$ for block mode
                const delimiter = mode === 'inline' ? '$' : '$$';
                latexContent = `${delimiter}${latexContent}${delimiter}`;
            }
        }

        // Auto-fix: Convert tabular to array (Katex supports array in math mode)
        if (latexContent.includes('\\begin{tabular}')) {
            latexContent = latexContent
                .replace(/\\begin{tabular}/g, '\\begin{array}')
                .replace(/\\end{tabular}/g, '\\end{array}');

            // Ensure it's wrapped in math mode if not already
            if (!latexContent.includes('$$') && !latexContent.includes('$')) {
                latexContent = `$$${latexContent}$$`;
            }
        }

        return (
            <div
                className={`latex-content ${mode === 'block' ? 'latex-block' : 'latex-inline'} ${className}`}
                style={{ fontSize: '1.25em' }}
            >
                <Latex strict={false}>{latexContent}</Latex>
            </div>
        );
    }

    // For inline mode, parse simple notation
    if (mode === 'inline' || mode === 'auto') {
        // Replace simple superscripts: ^2 -> ²
        let parsed = content
            .replace(/\^(\d+)/g, (match, num) => {
                const superscripts = '⁰¹²³⁴⁵⁶⁷⁸⁹';
                return num.split('').map(d => superscripts[d]).join('');
            })
            .replace(/\^{(\d+)}/g, (match, num) => {
                const superscripts = '⁰¹²³⁴⁵⁶⁷⁸⁹';
                return num.split('').map(d => superscripts[d]).join('');
            })
            // Replace simple subscripts: _n -> ₙ
            .replace(/_(\d+)/g, (match, num) => {
                const subscripts = '₀₁₂₃₄₅₆₇₈₉';
                return num.split('').map(d => subscripts[d]).join('');
            })
            .replace(/_{(\d+)}/g, (match, num) => {
                const subscripts = '₀₁₂₃₄₅₆₇₈₉';
                return num.split('').map(d => subscripts[d]).join('');
            });

        return (
            <span className={`latex-content latex-inline ${className}`}>
                {parsed}
            </span>
        );
    }

    return (
        <span className={`latex-content ${className}`}>
            <Latex>{content}</Latex>
        </span>
    );
};

export default LatexRenderer;
